import React from "react";
import { Card, CardContent } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { FileText, Droplet, Waves, Trash2, Zap, Flame } from "lucide-react";
import { cn } from "@shared/lib/utils";

const SERVICE_CONFIG: Record<string, { icon: any; theme: string }> = {
  water: { icon: Droplet, theme: "blue" },
  sewer: { icon: Waves, theme: "teal" },
  trash: { icon: Trash2, theme: "green" },
  electricity: { icon: Zap, theme: "purple" },
  gas: { icon: Flame, theme: "orange" },
};
const FALLBACK_CONFIG = { icon: FileText, theme: "blue" };

interface CoveredServiceItem {
  id: string;
  name: string;
  icon?: any;
  theme?: string;
}

export function PlanDetails({
  planConfig,
  planData,
}: {
  planConfig?: any;
  planData?: any;
}) {
  console.log("planData", planData);
  const coveredServices = React.useMemo(() => {
    const res = planConfig?.result || planConfig;
    const rates = res?.rates || res?.utilityServiceData?.rate;
    if (rates && typeof rates === "object") {
      return Object.keys(rates).map((key): CoveredServiceItem => {
        const name = key
          ? String(key)
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())
          : "N/A";
        return { id: key, name };
      });
    }
    const utilityService = res?.utilityService || res?.utility_service;
    if (Array.isArray(utilityService)) {
      return utilityService.map((item: any): CoveredServiceItem => {
        const name = typeof item === "string" ? item : item?.name || "N/A";
        return { id: name, name };
      });
    }
    return [];
  }, [planConfig]);

  const res = planConfig?.result || planConfig;
  const planName = res?.planName || "N/A";
  const planCode = res?.shortName || "N/A";
  const categoryDisplay = res?.categoryDisplay || planData?.category || "N/A";
  const statusDisplay =
    res?.statusDisplay ||
    planData?.statusDisplay ||
    (res?.isActive ? "Active" : "N/A");

  return (
    <Card className="shadow-sm border-gray-200 mb-6 overflow-hidden rounded-xl bg-white">
      <CardContent className="p-0">
        <div className="p-2">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className="w-9 h-9 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center shrink-0 border border-blue-100">
                <FileText className="w-4 h-4" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-base font-semibold text-gray-900">
                    {planName}
                  </h2>
                  <Badge
                    variant="secondary"
                    className="bg-blue-100 text-blue-700 border-0 text-xs font-semibold px-2 py-0.5 rounded-full"
                  >
                    {categoryDisplay}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 font-medium">{planCode}</p>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="bg-green-100 text-green-700 border-0 font-semibold px-2.5 py-0.5 rounded-full text-xs"
            >
              {statusDisplay}
            </Badge>
          </div>
        </div>

        {coveredServices.length > 0 && (
          <div className="px-5 py-3.5 border-t border-gray-100 bg-gray-50/30">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">
              Covered Services
            </p>
            <div className="flex flex-wrap gap-2.5">
              {coveredServices.map((s) => {
                const configId = s?.id ? String(s.id).toLowerCase() : null;
                const configName = s?.name
                  ? String(s.name).toLowerCase()
                  : null;
                const config =
                  (configId ? SERVICE_CONFIG[configId] : undefined) ||
                  (configName ? SERVICE_CONFIG[configName] : undefined) ||
                  FALLBACK_CONFIG;
                const Icon = s?.icon || config.icon;
                const currentTheme = s?.theme || config.theme;

                const themeColors: Record<string, string> = {
                  blue: "bg-blue-50 text-blue-600 border-blue-200",
                  teal: "bg-teal-50 text-teal-600 border-teal-200",
                  green: "bg-green-50 text-green-600 border-green-200",
                  purple: "bg-purple-50 text-purple-600 border-purple-200",
                  orange: "bg-orange-50 text-orange-600 border-orange-200",
                };
                return (
                  <Badge
                    key={s.id}
                    variant="outline"
                    className={cn(
                      "gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full",
                      themeColors[currentTheme] || themeColors.blue,
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" /> {s.name}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
